import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { ConfluenceUpdatePageCommandProperties } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';

import type { DocumentImageUpdateCommandProcessor } from '../../stream-processors/update-image-command-processor';
import type { AbortReason } from '../../types/abort-reason';
import type { IMAGE_CREATE_KEY } from '../../types/command-manager';
import type { CwrRemixVisualMetadata } from '../../types/cwr-remix-result';
import type { PageUpdateStrategy } from '../page-update-strategy';

type ImageUpdatePayload = Extract<
	ConfluenceUpdatePageCommandProperties,
	{ key: typeof IMAGE_CREATE_KEY }
>;

type RecordCwrRemixVisualMetadata = (
	singleInstrumentationId: string,
	resultId: string,
	metadata: CwrRemixVisualMetadata,
) => void;

export class ImageUpdateStrategy implements PageUpdateStrategy {
	constructor(
		private processor: DocumentImageUpdateCommandProcessor,
		private recordCwrRemixVisualMetadata?: RecordCwrRemixVisualMetadata | undefined,
	) {}

	async process(payload: ConfluenceUpdatePageCommandProperties): Promise<void> {
		const {
			data,
			isStreamComplete,
			traceId,
			singleInstrumentationId,
			isInlineEdit,
			chatRemixInstrumentationId,
			cwrRemixInvokedFrom,
		} = payload as ImageUpdatePayload;
		if (
			cwrRemixInvokedFrom &&
			singleInstrumentationId &&
			isExperimentEnabled('platform_editor_cwr_remix_acceptance')
		) {
			this.recordCwrRemixVisualMetadata?.(singleInstrumentationId, data.mediaFileId, {
				dynamicUiType: data.dynamicUiType ?? 'image',
				...(data.dynamicUiSubtype && { dynamicUiSubtype: data.dynamicUiSubtype }),
			});
		}
		await this.processor.streamChunk(
			data,
			isStreamComplete,
			traceId,
			singleInstrumentationId,
			isInlineEdit,
			chatRemixInstrumentationId,
		);
	}

	abort(reason?: AbortReason): void {
		// Skip timeout-based aborts — image generation naturally takes longer than
		// the inactivity timeout window (30s). Every other reason still aborts.
		if (reason === 'timeout') {
			return;
		}
		this.processor.abortAndClearStreamingState(reason);
	}
}
